import { useState, useEffect } from "react"
import { Navigate, Outlet } from "react-router-dom"
import { useAuthStore } from "@/stores/auth"
import { useGetUserQuery } from "@/queries/user"
import { isSuperAdmin } from "@/constants/user-types"
import { AppBootstrapLoader } from "@/components/loaders/preloader"
import { LOGIN } from "@/constants/paths/shared"

const REHYDRATE_WAIT_MS = 1500

/**
 * Protects admin routes. Requires user to be WOEZOR_SUPER or WOEZOR_ADMIN.
 * Uses auth store user first (from login). Only redirects when we have a
 * definitive non-admin (user with userType that is not super admin), so we
 * don't redirect before store rehydration or when profile returns minimal data.
 */
export function AdminGuard() {
  const token = useAuthStore((state) => state.token)
  const storeUser = useAuthStore((state) => state.user)
  const canSkipProfile = !!(token && storeUser && isSuperAdmin(storeUser))
  const { data, isLoading, isError } = useGetUserQuery({}, { skip: !token || canSkipProfile })
  const profileUser = data as { userType?: string | string[] } | undefined
  const user = profileUser ?? storeUser

  const [rehydrationWaited, setRehydrationWaited] = useState(false)
  useEffect(() => {
    if (data !== null || storeUser) return
    const t = setTimeout(() => setRehydrationWaited(true), REHYDRATE_WAIT_MS)
    return () => clearTimeout(t)
  }, [storeUser, data])

  if (!token) {
    return <Navigate to={LOGIN} replace />
  }

  if (storeUser && isSuperAdmin(storeUser)) {

    return <Outlet />
  }

  if (profileUser && isSuperAdmin(profileUser)) {

    return <Outlet />
  }

  // Profile returned null/empty (API returned empty string) but store has admin – use store so we don't stay stuck
  if ((data === null || !profileUser) && storeUser && isSuperAdmin(storeUser)) {

    return <Outlet />
  }

  // Profile returned minimal/empty data (no userType) but store has admin – use store so we don't stay stuck on preloader
  if (profileUser && profileUser.userType == null && storeUser && isSuperAdmin(storeUser)) {

    return <Outlet />
  }

  // Network errors: fall back to store user instead of redirecting away from admin.
  if (isError && storeUser && isSuperAdmin(storeUser)) {
    return <Outlet />
  }

  if (isLoading) {

    return <AppBootstrapLoader />
  }

  // Only redirect when we have a clear non-admin (user has userType and it's not super admin).
  const hasDefinitiveNonAdmin =
    user?.userType != null && !isSuperAdmin(user)
  if (hasDefinitiveNonAdmin) {

    return <Navigate to="/dashboard" replace />
  }

  // Profile returned empty and we have no store user yet – wait for rehydration so we don't redirect before store loads
  if (data === null && !storeUser) {
    if (!rehydrationWaited) {
      return <AppBootstrapLoader />
    }
    // If we've waited and still have nothing, they likely aren't an admin
    return <Navigate to="/dashboard" replace />
  }

  // Final fallback: if we have a user but it's not a super admin, send to dashboard.
  // Otherwise, if we're still waiting on data, show preloader.
  if (isLoading) return <AppBootstrapLoader />

  return <Navigate to="/dashboard" replace />
}

export default AdminGuard
